"use strict";

(() => {
  const popupState = {
    previousScrollY: window.scrollY,
    direction: "down",
    globalEventsBound: false,
  };

  const getPopup = (input) => {
    const element = input instanceof Event ? input.currentTarget : input;
    if (!(element instanceof Element)) return null;
    return element.matches(".popup") ? element : element.closest(".popup");
  };

  const getNumber = (element, key, fallback = 0) => {
    const value = Number.parseFloat(element?.dataset?.[key]);
    return Number.isFinite(value) ? value : fallback;
  };

  const getStorage = (key) => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return undefined;
      const parsed = JSON.parse(item);
      if (parsed.expires && Date.now() > parsed.expires) {
        localStorage.removeItem(key);
        return undefined;
      }
      return parsed.value;
    } catch {
      return undefined;
    }
  };

  const setStorage = (key, value, options = {}) => {
    try {
      const data = { value, expires: null };
      const lifetimeDays = Number.parseInt(options.expires, 10);
      if (Number.isFinite(lifetimeDays) && lifetimeDays > 0) {
        const expiresDate = new Date();
        expiresDate.setDate(expiresDate.getDate() + lifetimeDays);
        data.expires = expiresDate.getTime();
      }
      localStorage.setItem(key, JSON.stringify(data));
    } catch {}
  };

  const getScrollPercent = () => {
    const root = document.documentElement;
    const body = document.body;
    const scrollTop = root.scrollTop || body.scrollTop;
    const scrollHeight = Math.max(root.scrollHeight, body.scrollHeight);
    const available = scrollHeight - root.clientHeight;
    return available > 0 ? (scrollTop / available) * 100 : 0;
  };

  const animationTimeMs = (element) => {
    if (!element) return 0;
    const style = window.getComputedStyle(element);
    const parseTimes = (value) =>
      value.split(",").map((entry) => {
        const token = entry.trim();
        return token.endsWith("ms")
          ? Number.parseFloat(token)
          : Number.parseFloat(token) * 1000;
      });
    const durations = parseTimes(style.animationDuration);
    const delays = parseTimes(style.animationDelay);
    return Math.max(
      0,
      ...durations.map((duration, index) => duration + (delays[index] ?? delays[0] ?? 0))
    );
  };

  const animateOnce = (element, classNames, callback) => {
    if (!element) {
      callback?.();
      return;
    }
    const classes = classNames.filter(Boolean);
    element.classList.add(...classes);
    let completed = false;
    const finish = () => {
      if (completed) return;
      completed = true;
      element.removeEventListener("animationend", finish);
      element.removeEventListener("webkitAnimationEnd", finish);
      element.classList.remove(...classes);
      callback?.();
    };
    element.addEventListener("animationend", finish, { once: true });
    element.addEventListener("webkitAnimationEnd", finish, { once: true });
    window.setTimeout(finish, Math.max(50, animationTimeMs(element) + 50));
  };

  const applyAnimation = (popup, name, callback) => {
    const overlayName = callback ? "popupExitFade" : "popupOpenFade";
    const overlay = popup.nextElementSibling?.matches(".popup-overlay")
      ? popup.nextElementSibling
      : null;
    animateOnce(overlay, ["popup-animated", overlayName]);
    animateOnce(popup.querySelector(".popup-wrap"), ["popup-animated", name], callback);
  };

  const closePopup = (input) => {
    const popup = getPopup(input);
    if (!popup || !popup.classList.contains("popup-open")) return;
    applyAnimation(popup, popup.dataset.exitAnimation, () => {
      popup.classList.add("popup-already-opened");
      popup.classList.remove("popup-open");
      document.body.classList.remove("popup-scroll-hidden");
    });
  };

  const bindCloseTrigger = (popup) => {
    const trigger = popup.dataset.closeTrigger;
    if (trigger === "delay") {
      window.setTimeout(
        () => closePopup(popup),
        getNumber(popup, "closeDelayNumber") * 1000
      );
    }
    if (trigger === "scroll" && popup.dataset.closeScrollBound !== "true") {
      popup.dataset.closeScrollBound = "true";
      document.addEventListener("scroll", () => {
        if (!popup.classList.contains("popup-open")) return;
        const type = popup.dataset.closeScrollType;
        const distance = getNumber(popup, "closeScrollPosition");
        const initialPx = getNumber(popup, "initScrollPx");
        const initialPercent = getNumber(popup, "initScrollPercent");
        const currentPercent = getScrollPercent();
        if (
          (type === "px" && popupState.direction === "up" && window.scrollY < initialPx - distance) ||
          (type === "px" && popupState.direction === "down" && window.scrollY >= initialPx + distance) ||
          (type === "%" && popupState.direction === "up" && currentPercent < initialPercent - distance) ||
          (type === "%" && popupState.direction === "down" && currentPercent >= initialPercent + distance)
        ) {
          closePopup(popup);
        }
      }, { passive: true });
    }
  };

  const openPopup = (popup) => {
    if (!(popup instanceof Element)) return;
    const storageKey = `popup-${popup.dataset.id || "0"}`;
    const limit = Number.parseInt(getStorage(storageKey) || 0, 10) + 1;
    setStorage(storageKey, limit, { expires: popup.dataset.limitLifetime });
    if (
      popup.classList.contains("popup-open") ||
      popup.classList.contains("popup-already-opened")
    ) {
      return;
    }
    if (popup.dataset.bodyScrollDisable === "true") {
      document.body.classList.add("popup-scroll-hidden");
    }
    popup.classList.add("popup-open");
    popup.dataset.initScrollPx = String(window.scrollY);
    popup.dataset.initScrollPercent = String(getScrollPercent());
    applyAnimation(popup, popup.dataset.openAnimation);
    bindCloseTrigger(popup);
  };

  const isAllowed = (popup) => {
    const limit = getNumber(popup, "limitDisplay");
    const seen = Number.parseInt(getStorage(`popup-${popup.dataset.id || "0"}`), 10);
    return !(limit && seen && seen >= limit);
  };

  const bindOpenTrigger = (popup) => {
    const trigger = popup.dataset.openTrigger;
    if (trigger === "none") {
      if (!getStorage(`popup-age-${popup.dataset.id || "0"}`)) openPopup(popup);
    } else if (trigger === "delay") {
      window.setTimeout(
        () => openPopup(popup),
        getNumber(popup, "openDelayNumber") * 1000
      );
    } else if (trigger === "exit" && popup.dataset.exitIntentBound !== "true") {
      popup.dataset.exitIntentBound = "true";
      let enabled = true;
      document.addEventListener("mousemove", (event) => {
        const scroll = window.pageYOffset || document.documentElement.scrollTop;
        if (enabled && event.pageY - scroll < 7) {
          openPopup(popup);
          enabled = false;
        }
      });
    } else if (trigger === "scroll" && popup.dataset.openScrollBound !== "true") {
      popup.dataset.openScrollBound = "true";
      document.addEventListener("scroll", () => {
        const type = popup.dataset.openScrollType;
        const position = getNumber(popup, "openScrollPosition");
        if (
          (type === "px" && window.scrollY >= position) ||
          (type === "%" && getScrollPercent() >= position)
        ) {
          openPopup(popup);
        }
      }, { passive: true });
    } else if (trigger === "accept") {
      if (!getStorage(`popup-accept-${popup.dataset.id || "0"}`)) openPopup(popup);
    }
  };

  const bindManualTrigger = (popup) => {
    if (popup.dataset.openTrigger !== "manual" || popup.dataset.manualBound === "true") return;
    const selector = popup.dataset.openManualSelector;
    if (!selector) return;
    let triggers;
    try {
      triggers = document.querySelectorAll(selector);
    } catch {
      return;
    }
    popup.dataset.manualBound = "true";
    triggers.forEach((trigger) => {
      trigger.classList.add("popup-trigger");
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        popup.classList.remove("popup-already-opened");
        openPopup(popup);
      });
    });
  };

  const initializePopups = () => {
    document.querySelectorAll(".popup").forEach((popup) => {
      bindManualTrigger(popup);
      if (!isAllowed(popup) && !popup.classList.contains("age-verification")) return;
      if (popup.dataset.popupInitialized === "true") return;
      popup.dataset.popupInitialized = "true";
      bindOpenTrigger(popup);
    });
  };

  const bindGlobalEvents = () => {
    if (popupState.globalEventsBound) return;
    popupState.globalEventsBound = true;
    document.addEventListener("scroll", () => {
      const current = window.scrollY;
      popupState.direction = current > popupState.previousScrollY ? "down" : "up";
      popupState.previousScrollY = current;
    }, { passive: true });

    document.addEventListener("click", (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const ageClose = target.closest(".age-verification__popup-close");
      const accept = target.closest(".popup-accept");
      const close = target.closest(".popup-close");
      const decline = target.closest(".age-verification__button-no");
      if (ageClose) {
        const popup = getPopup(ageClose);
        if (popup) setStorage(`popup-age-${popup.dataset.id || "0"}`, 1, { expires: 360 });
        closePopup(ageClose);
      } else if (accept) {
        const popup = getPopup(accept);
        if (popup) setStorage(`popup-accept-${popup.dataset.id || "0"}`, 1, { expires: 360 });
        closePopup(accept);
      } else if (close) {
        closePopup(close);
      }
      if (decline) {
        document.querySelectorAll(".age-verification__question").forEach((element) =>
          element.classList.remove("show")
        );
        document.querySelectorAll(".age-verification__declined").forEach((element) =>
          element.classList.add("show")
        );
      }
      if (target.closest(".popup-overlay")) {
        document
          .querySelectorAll('.popup-open[data-overlay-close="true"]')
          .forEach(closePopup);
      }
    });

    document.addEventListener("keyup", (event) => {
      if (event.key === "Escape") {
        document.querySelectorAll('.popup-open[data-esc-close="true"]').forEach(closePopup);
      } else if (event.key === "F4") {
        document.querySelectorAll('.popup-open[data-f4-close="true"]').forEach(closePopup);
      }
    });
    document.addEventListener("shopify:section:load", initializePopups);
    document.addEventListener("shopify:section:unload", () => {
      document.body.classList.remove("popup-scroll-hidden");
    });
  };

  const initialize = () => {
    bindGlobalEvents();
    initializePopups();
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }
})();
